if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      #zoomPointer = {
        id: null,
        img: null,
        startX: 0,
        startY: 0,
        originPan: { x: 0, y: 0 },
        moved: false,
        wasZoomed: false,
      };

      #zoomScale = 2;
      #tapThreshold = 8;

      constructor() {
        super();
        this.addEventListener('pointerdown', this.#onPointerDown.bind(this));
        this.addEventListener('pointermove', this.#onPointerMove.bind(this));
        this.addEventListener('pointerup', this.#onPointerUp.bind(this));
        this.addEventListener('pointercancel', this.#onPointerUp.bind(this));
      }

      #modalImageFromEvent(event) {
        const content = this.querySelector('.product-media-modal__content');
        if (!content) return null;
        const img = event.target.closest('img');
        if (!img || img.parentElement !== content) return null;
        return img;
      }

      #getPan(img) {
        const x = parseFloat(img.style.getPropertyValue('--modal-pan-x')) || 0;
        const y = parseFloat(img.style.getPropertyValue('--modal-pan-y')) || 0;
        return { x, y };
      }

      #setPan(img, x, y) {
        const { x: cx, y: cy } = this.#clampPan(img, x, y);
        img.style.setProperty('--modal-pan-x', `${cx}px`);
        img.style.setProperty('--modal-pan-y', `${cy}px`);
      }

      #clearPan(img) {
        img.style.removeProperty('--modal-pan-x');
        img.style.removeProperty('--modal-pan-y');
      }

      #clampPan(img, x, y) {
        const content = img.parentElement;
        if (!content) return { x: 0, y: 0 };
        const iw = img.offsetWidth;
        const ih = img.offsetHeight;
        const cw = content.clientWidth;
        const ch = content.clientHeight;
        const s = this.#zoomScale;
        const maxX = Math.max(0, (iw * s - cw) / 2);
        const maxY = Math.max(0, (ih * s - ch) / 2);
        return {
          x: Math.min(maxX, Math.max(-maxX, x)),
          y: Math.min(maxY, Math.max(-maxY, y)),
        };
      }

      #onPointerDown(event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        const img = this.#modalImageFromEvent(event);
        if (!img) return;

        this.#zoomPointer.id = event.pointerId;
        this.#zoomPointer.img = img;
        this.#zoomPointer.startX = event.clientX;
        this.#zoomPointer.startY = event.clientY;
        this.#zoomPointer.originPan = this.#getPan(img);
        this.#zoomPointer.moved = false;
        this.#zoomPointer.wasZoomed = img.classList.contains('is-modal-zoomed');

        if (this.#zoomPointer.wasZoomed) {
          img.classList.add('is-modal-panning');
          try {
            img.setPointerCapture(event.pointerId);
          } catch (_) {}
        }
      }

      #onPointerMove(event) {
        if (event.pointerId !== this.#zoomPointer.id || !this.#zoomPointer.img) return;
        const img = this.#zoomPointer.img;
        if (!img.classList.contains('is-modal-zoomed')) return;

        const dx = event.clientX - this.#zoomPointer.startX;
        const dy = event.clientY - this.#zoomPointer.startY;
        if (Math.abs(dx) > 4 || Math.abs(dy) > 4) this.#zoomPointer.moved = true;

        const x = this.#zoomPointer.originPan.x + dx;
        const y = this.#zoomPointer.originPan.y + dy;
        this.#setPan(img, x, y);
      }

      #onPointerUp(event) {
        if (event.pointerId !== this.#zoomPointer.id || !this.#zoomPointer.img) return;

        const img = this.#zoomPointer.img;
        const moved =
          this.#zoomPointer.moved ||
          Math.abs(event.clientX - this.#zoomPointer.startX) > this.#tapThreshold ||
          Math.abs(event.clientY - this.#zoomPointer.startY) > this.#tapThreshold;

        if (img.classList.contains('is-modal-zoomed')) {
          try {
            img.releasePointerCapture(event.pointerId);
          } catch (_) {}
          img.classList.remove('is-modal-panning');
        }

        if (this.#zoomPointer.wasZoomed) {
          if (!moved) {
            img.classList.remove('is-modal-zoomed');
            this.#clearPan(img);
          }
        } else if (!moved) {
          img.classList.add('is-modal-zoomed');
          this.#clearPan(img);
          this.#setPan(img, 0, 0);
        }

        this.#zoomPointer.id = null;
        this.#zoomPointer.img = null;
      }

      hide() {
        this.querySelectorAll('.product-media-modal__content > img').forEach((img) => {
          img.classList.remove('is-modal-zoomed', 'is-modal-panning');
          this.#clearPan(img);
        });
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        this.querySelectorAll('.product-media-modal__content > img').forEach((img) => {
          img.classList.remove('is-modal-zoomed', 'is-modal-panning');
          this.#clearPan(img);
        });

        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        if (!activeMedia || activeMedia.hidden) return;

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        const mediaWidth =
          activeMedia.nodeName === 'IMG'
            ? activeMedia.offsetWidth
            : (activeMedia.querySelector('img')?.offsetWidth ?? activeMedia.offsetWidth ?? activeMedia.width);
        container.scrollLeft = (mediaWidth - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }
    }
  );
}
